import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";
import { useAppData } from "../context/AppDataContext";

function formatDate(dateStr) {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
}

export default function TravelogueDetail() {
  const { travelogueKey } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "hr";
  const { travelogues } = useAppData();
  const travelogue = travelogues.find((p) => p.key === travelogueKey);
  const gallery = travelogue?.gallery ?? [];

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isLightboxOpen = lightboxIndex !== null;

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = () => setLightboxIndex((i) => (i <= 0 ? gallery.length - 1 : i - 1));
  const goNext = () => setLightboxIndex((i) => (i >= gallery.length - 1 ? 0 : i + 1));

  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (i <= 0 ? gallery.length - 1 : i - 1));
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i >= gallery.length - 1 ? 0 : i + 1));
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isLightboxOpen, gallery.length]);

  if (!travelogue) {
    return (
      <div className="app">
        <Navbar />
        <main className="travelogue-page">
          <section className="section" style={{ paddingTop: "6rem" }}>
            <div className="container">
              <h1>{t("blog.notFound") || "Putopis nije pronađen"}</h1>
              <button onClick={() => navigate("/travelogue")} className="btn primary">
                ← {t("blog.btnBack") || "Natrag na putopise"}
              </button>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const base = lng.slice(0, 2);
  const title = travelogue.title[base] || travelogue.title.en;
  const content = travelogue.content[base] || travelogue.content.en;

  return (
    <div className="app">
      <SEO
        title={`${title} - TraveLina`}
        description={content.slice(0, 160).replace(/\s+/g, " ")}
        keywords={`putopis, ${title}, TraveLina, putovanja`}
      />
      <Navbar />
      <main className="travelogue-page">
        <section className="section" style={{ paddingTop: "6rem" }}>
          <div className="container">
            <button
              type="button"
              className="btn-page-back"
              onClick={() => navigate("/travelogue")}
            >
              ← {t("blog.btnBack") || "Natrag na putopise"}
            </button>

            <article className="travelogue-article">
              <div
                className="travelogue-hero travelogue-hero--detail"
                style={{
                  backgroundImage: `url(${travelogue.image})`,
                }}
              >
                <div className="travelogue-hero-gradient travelogue-hero-gradient--detail" aria-hidden />
                <h1 className="travelogue-hero-title travelogue-hero-title--detail">{title}</h1>
              </div>

              <div className="travelogue-meta travelogue-meta--detail">
                <time>{formatDate(travelogue.date, lng)}</time>
              </div>

              <div className="travelogue-content">
                {content}
              </div>

              {gallery.length > 0 && (
                <div className="travelogue-gallery">
                  <div className="travelogue-gallery-grid">
                    {gallery.map((img, idx) => (
                      <button
                        type="button"
                        key={idx}
                        className="travelogue-gallery-item"
                        onClick={() => openLightbox(idx)}
                      >
                        <img
                          src={img}
                          alt=""
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isLightboxOpen && (
                <div
                  className="travelogue-lightbox-overlay"
                  onClick={closeLightbox}
                  role="dialog"
                  aria-modal="true"
                  aria-label={t("tourPopup.close") || "Zatvori"}
                >
                  <button
                    type="button"
                    className="travelogue-lightbox-close"
                    onClick={closeLightbox}
                    aria-label={t("tourPopup.close") || "Zatvori"}
                  >
                    ×
                  </button>
                  <button
                    type="button"
                    className="travelogue-lightbox-prev"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    aria-label="Prethodna"
                  >
                    ‹
                  </button>
                  <div className="travelogue-lightbox-content" onClick={(e) => e.stopPropagation()}>
                    <img
                      src={gallery[lightboxIndex]}
                      alt=""
                      className="travelogue-lightbox-img"
                    />
                    <span className="travelogue-lightbox-counter">
                      {lightboxIndex + 1} / {gallery.length}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="travelogue-lightbox-next"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    aria-label="Sljedeća"
                  >
                    ›
                  </button>
                </div>
              )}

              {((travelogue.videos && travelogue.videos.length > 0) || travelogue.video) && (
                <div className="travelogue-videos">
                  {(travelogue.videos || (travelogue.video ? [travelogue.video] : [])).map((src, idx) => (
                    <div key={idx} className="travelogue-video">
                      <video
                        className="travelogue-video-player"
                        src={src}
                        controls
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  ))}
                </div>
              )}
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
